import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  fetchVpnAdminPeers,
  fetchVpnAdminStatus,
  fetchVpnGate,
  fetchVpnInfo,
  getApiErrorMessage,
  revokeVpnAdminClient,
  saveVpnGate,
} from "../api";
import ErrorRetry from "../components/ErrorRetry";

const STATUS_LABELS = {
  active: "Активен",
  pending: "Ожидает",
  failed: "Ошибка",
  revoke_failed: "Отзыв не удался",
  revoked: "Отозван",
};

const STATUS_FILTERS = [
  { value: "all", label: "Все статусы" },
  { value: "active", label: "Активные" },
  { value: "pending", label: "Ожидают" },
  { value: "failed", label: "Ошибки" },
  { value: "revoke_failed", label: "Сбой отзыва" },
  { value: "revoked", label: "Отозванные" },
  { value: "wg", label: "В WG" },
];

const REFRESH_MS = 45_000;

function formatBytes(value) {
  const bytes = Number(value) || 0;
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 ** 2) return `${(bytes / 1024).toFixed(1)} KB`;
  if (bytes < 1024 ** 3) return `${(bytes / 1024 ** 2).toFixed(1)} MB`;
  return `${(bytes / 1024 ** 3).toFixed(2)} GB`;
}

function formatDateTime(value) {
  if (!value) return "—";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "—";
  return date.toLocaleString("ru-RU", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function shortKey(publicKey) {
  if (!publicKey) return "—";
  return publicKey.length > 12 ? `${publicKey.slice(0, 8)}…` : publicKey;
}

function canRevoke(peer) {
  return (
    peer.source === "site" &&
    (peer.status === "active" || peer.status === "revoke_failed")
  );
}

function peerRowKey(peer) {
  return peer.id || peer.public_key;
}

export default function VpnAdmin() {
  const [status, setStatus] = useState(null);
  const [publicInfo, setPublicInfo] = useState(null);
  const [peers, setPeers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [actionError, setActionError] = useState("");
  const [actionOk, setActionOk] = useState("");
  const [query, setQuery] = useState("");
  const [showLegacy, setShowLegacy] = useState(false);
  const [statusFilter, setStatusFilter] = useState("all");
  const [revokeTarget, setRevokeTarget] = useState(null);
  const [revoking, setRevoking] = useState(false);
  const [gate, setGate] = useState(null);
  const [gateError, setGateError] = useState("");
  const [gatePassword, setGatePassword] = useState("");
  const [savingGate, setSavingGate] = useState(false);

  const loadData = useCallback(async ({ soft = false } = {}) => {
    if (!soft) setLoading(true);
    setError("");
    try {
      const [statusData, peersData, infoData] = await Promise.all([
        fetchVpnAdminStatus(),
        fetchVpnAdminPeers(),
        fetchVpnInfo().catch(() => null),
      ]);
      setStatus(statusData);
      setPeers(Array.isArray(peersData) ? peersData : []);
      setPublicInfo(infoData);
    } catch (err) {
      setError(getApiErrorMessage(err, "Не удалось загрузить данные VPN"));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  useEffect(() => {
    let cancelled = false;
    fetchVpnGate()
      .then((data) => {
        if (cancelled) return;
        setGate(data);
        setGateError("");
      })
      .catch((err) => {
        if (cancelled) return;
        setGateError(
          getApiErrorMessage(err, "Не удалось загрузить вход на страницу выдачи")
        );
      });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    const tick = () => {
      if (document.visibilityState !== "visible") return;
      if (revokeTarget) return;
      loadData({ soft: true });
    };
    const id = window.setInterval(tick, REFRESH_MS);
    return () => window.clearInterval(id);
  }, [loadData, revokeTarget]);

  useEffect(() => {
    if (!revokeTarget) return undefined;
    const onKey = (event) => {
      if (event.key !== "Escape" || revoking) return;
      setRevokeTarget(null);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [revokeTarget, revoking]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return peers.filter((peer) => {
      if (!showLegacy && peer.source === "legacy") return false;
      if (statusFilter === "wg") {
        if (peer.source !== "legacy") return false;
      } else if (statusFilter !== "all" && peer.status !== statusFilter) {
        return false;
      }
      if (!q) return true;
      const haystack = [
        peer.name,
        peer.device,
        peer.ip,
        peer.requester_ip,
        peer.public_key,
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();
      return haystack.includes(q);
    });
  }, [peers, query, showLegacy, statusFilter]);

  const siteCount = peers.filter((peer) => peer.source === "site").length;
  const legacyCount = peers.filter((peer) => peer.source === "legacy").length;
  const dbCounts = status?.database || {};
  const inventory = status?.inventory || {};
  const issuanceEnabled =
    publicInfo?.enabled !== false && status?.enabled !== false;

  const handleSaveGate = async (password) => {
    setSavingGate(true);
    setActionError("");
    setActionOk("");
    try {
      const saved = await saveVpnGate(password);
      setGate(saved);
      setGatePassword("");
      setActionOk("Прежние входы сброшены");
      try {
        const next = await fetchVpnGate();
        setGate(next);
      } catch {
        // Повторное чтение не отменяет уже сохранённый пароль.
      }
    } catch (err) {
      setActionError(
        getApiErrorMessage(err, "Не удалось сохранить пароль")
      );
    } finally {
      setSavingGate(false);
    }
  };

  const handleRevoke = async () => {
    if (!revokeTarget?.id) return;
    setRevoking(true);
    setActionError("");
    setActionOk("");
    try {
      await revokeVpnAdminClient(revokeTarget.id);
      setRevokeTarget(null);
      setActionOk(
        `Конфигурация ${revokeTarget.ip || revokeTarget.id.slice(0, 8)} отозвана`
      );
      await loadData({ soft: true });
    } catch (err) {
      setActionError(
        getApiErrorMessage(err, "Не удалось отозвать VPN-конфигурацию")
      );
    } finally {
      setRevoking(false);
    }
  };

  return (
    <div className="page-container vpn-admin-page">
      <div className="page-header vpn-admin-header">
        <div>
          <h2>VPN</h2>
          <p className="vpn-admin-subtitle">
            Все пиры AmneziaWG: выданные через сайт и существующие вне БД
          </p>
        </div>
        <div className="vpn-admin-header-actions">
          <Link to="/vpn" className="vpn-admin-add">
            Добавить пользователя
          </Link>
          <button
            type="button"
            className="vpn-admin-refresh"
            onClick={() => loadData()}
            disabled={loading}
          >
            {loading ? "Обновление…" : "Обновить"}
          </button>
        </div>
      </div>

      {error && <ErrorRetry message={error} onRetry={() => loadData()} />}

      {actionOk && !revokeTarget && (
        <div className="vpn-admin-banner vpn-admin-banner--ok" role="status">
          {actionOk}
        </div>
      )}
      {actionError && !revokeTarget && (
        <div className="vpn-admin-banner vpn-admin-banner--error" role="alert">
          {actionError}
        </div>
      )}

      {!error && (
        <section className="vpn-admin-status" aria-label="Статус VPN">
          <div className="vpn-admin-status-main">
            <span
              className={`vpn-admin-dot ${
                status?.agent === "ok"
                  ? "vpn-admin-dot--ok"
                  : "vpn-admin-dot--bad"
              }`}
            />
            <div>
              <div className="vpn-admin-status-title">
                {status?.agent === "ok" ? "Agent работает" : "Agent недоступен"}
              </div>
              <div className="vpn-admin-status-meta">
                Публичная выдача:{" "}
                <strong>{issuanceEnabled ? "включена" : "выключена"}</strong>
              </div>
            </div>
          </div>
          <div className="vpn-admin-status-metrics">
            <div className="vpn-admin-metric">
              <span>Config peers</span>
              <strong>{inventory.config_peers ?? "—"}</strong>
            </div>
            <div className="vpn-admin-metric">
              <span>Runtime peers</span>
              <strong>{inventory.runtime_peers ?? "—"}</strong>
            </div>
            <div className="vpn-admin-metric">
              <span>Сайт</span>
              <strong>{siteCount}</strong>
            </div>
            <div className="vpn-admin-metric">
              <span>Вне сайта</span>
              <strong>{legacyCount}</strong>
            </div>
            <div className="vpn-admin-metric">
              <span>Active (БД)</span>
              <strong>{dbCounts.active ?? 0}</strong>
            </div>
            <div className="vpn-admin-metric">
              <span>Revoked (БД)</span>
              <strong>{dbCounts.revoked ?? 0}</strong>
            </div>
          </div>
        </section>
      )}

      {(gate || gateError) && (
        <section className="vpn-admin-status" aria-labelledby="vpn-gate-title">
          <div>
            <h3
              id="vpn-gate-title"
              className="vpn-admin-status-title"
              style={{ margin: 0 }}
            >
              Вход на страницу выдачи
            </h3>
            {gate && (
              <p className="vpn-admin-status-meta">
                {gate.enabled ? "Закрыто паролем" : "Сейчас открыто"}
              </p>
            )}
          </div>
          {gateError && (
            <div className="vpn-admin-banner vpn-admin-banner--error" role="alert">
              {gateError}
            </div>
          )}
          {gate && (
            <div className="vpn-admin-toolbar">
              <label className="vpn-admin-search">
                <span className="vpn-admin-status-meta">Пароль</span>
                <input
                  type="password"
                  value={gatePassword}
                  onChange={(event) => setGatePassword(event.target.value)}
                  autoComplete="new-password"
                  disabled={savingGate}
                />
              </label>
              <button
                type="button"
                className="vpn-admin-refresh"
                onClick={() => handleSaveGate(gatePassword)}
                disabled={savingGate}
              >
                Сохранить
              </button>
              {gate.enabled && (
                <button
                  type="button"
                  className="vpn-admin-revoke"
                  onClick={() => handleSaveGate("")}
                  disabled={savingGate}
                >
                  Убрать пароль
                </button>
              )}
            </div>
          )}
        </section>
      )}

      <section className="vpn-admin-table-section">
        <div className="vpn-admin-toolbar">
          <label className="vpn-admin-search">
            <input
              type="search"
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              placeholder="Имя, IP, pubkey…"
              aria-label="Поиск пиров VPN"
            />
          </label>
          <label className="vpn-admin-filter">
            <span>Статус</span>
            <select
              value={statusFilter}
              onChange={(event) => setStatusFilter(event.target.value)}
              aria-label="Фильтр статуса"
            >
              {STATUS_FILTERS.filter(
                (item) => showLegacy || item.value !== "wg"
              ).map((item) => (
                <option key={item.value} value={item.value}>
                  {item.label}
                </option>
              ))}
            </select>
          </label>
          <button
            type="button"
            className={`vpn-admin-legacy-toggle${
              showLegacy ? " vpn-admin-legacy-toggle--on" : ""
            }`}
            onClick={() => {
              setShowLegacy((value) => {
                const next = !value;
                if (!next && statusFilter === "wg") setStatusFilter("all");
                return next;
              });
            }}
          >
            {showLegacy
              ? "Скрыть вне сайта"
              : `Показать вне сайта (${legacyCount})`}
          </button>
          <div className="vpn-admin-count">
            Показано {filtered.length}
            {showLegacy
              ? ` из ${peers.length}`
              : ` · скрыто вне сайта: ${legacyCount}`}
          </div>
        </div>

        {loading && peers.length === 0 ? (
          <div className="vpn-admin-empty">Загружаем пиров…</div>
        ) : filtered.length === 0 ? (
          <div className="vpn-admin-empty">
            {peers.length === 0
              ? "Пиры не найдены."
              : "Нет записей по текущему фильтру."}
          </div>
        ) : (
          <div className="vpn-admin-table-wrap">
            <table className="vpn-admin-table">
              <thead>
                <tr>
                  <th>Источник</th>
                  <th>Имя / устройство</th>
                  <th>IP</th>
                  <th>Статус</th>
                  <th>Handshake</th>
                  <th>Трафик ↓ / ↑</th>
                  <th>Pubkey</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {filtered.map((peer) => (
                  <tr key={peerRowKey(peer)}>
                    <td>
                      <span
                        className={`vpn-admin-badge vpn-admin-badge--${
                          peer.source === "site" ? "site" : "legacy"
                        }`}
                      >
                        {peer.source === "site" ? "Сайт" : "Вне сайта"}
                      </span>
                    </td>
                    <td>
                      <div className="vpn-admin-name">{peer.name || "—"}</div>
                      <div className="vpn-admin-id">
                        {peer.device || (peer.id ? peer.id.slice(0, 8) : "—")}
                      </div>
                    </td>
                    <td className="vpn-admin-mono">{peer.ip || "—"}</td>
                    <td>
                      {peer.source === "site" ? (
                        <>
                          <span
                            className={`vpn-admin-badge vpn-admin-badge--${peer.status}`}
                          >
                            {STATUS_LABELS[peer.status] || peer.status}
                          </span>
                          {peer.error_message && (
                            <div
                              className="vpn-admin-error-hint"
                              title={peer.error_message}
                            >
                              {peer.error_message}
                            </div>
                          )}
                        </>
                      ) : (
                        <span className="vpn-admin-badge vpn-admin-badge--wg">
                          {!peer.in_config
                            ? "только runtime"
                            : !peer.in_runtime
                              ? "только config"
                              : "в WG"}
                        </span>
                      )}
                    </td>
                    <td>{formatDateTime(peer.latest_handshake_at)}</td>
                    <td className="vpn-admin-mono">
                      {formatBytes(peer.transfer_in_bytes)}
                      {" / "}
                      {formatBytes(peer.transfer_out_bytes)}
                    </td>
                    <td
                      className="vpn-admin-mono"
                      title={peer.public_key || ""}
                    >
                      {shortKey(peer.public_key)}
                    </td>
                    <td className="vpn-admin-actions">
                      {canRevoke(peer) && (
                        <button
                          type="button"
                          className="vpn-admin-revoke"
                          onClick={() => {
                            setActionError("");
                            setActionOk("");
                            setRevokeTarget(peer);
                          }}
                        >
                          Отозвать
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      {revokeTarget && (
        <div
          className="users-modal-backdrop"
          onClick={() => !revoking && setRevokeTarget(null)}
        >
          <div
            className="users-modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="vpn-revoke-title"
            onClick={(event) => event.stopPropagation()}
          >
            <h3 id="vpn-revoke-title">Отозвать конфигурацию?</h3>
            <p className="settings-hint">
              Устройство{" "}
              <strong>
                {revokeTarget.name || "без имени"}
                {revokeTarget.device ? ` / ${revokeTarget.device}` : ""}
              </strong>{" "}
              с IP <strong className="vpn-admin-mono">{revokeTarget.ip}</strong>{" "}
              потеряет доступ к VPN. Это действие нельзя отменить из интерфейса.
            </p>
            {actionError && (
              <div className="login-error" style={{ marginTop: 12 }}>
                {actionError}
              </div>
            )}
            <div className="users-modal-actions">
              <button
                type="button"
                className="users-action-btn"
                onClick={() => setRevokeTarget(null)}
                disabled={revoking}
              >
                Отмена
              </button>
              <button
                type="button"
                className="cd-danger-btn"
                onClick={handleRevoke}
                disabled={revoking}
              >
                {revoking ? "Отзываем…" : "Отозвать"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
